import { useState } from 'react';
import { Loader2, X, GripVertical } from 'lucide-react';
import { getKpiInsight, STATUS_COLOR } from './utils/kpiInsight';
import HoverCard from '../../components/hoverCard/HoverCard';
import { Avatar } from '@fluentui/react-components';
import { displayDecimalFormatted, getAccountCurrency } from '../../utils/systemSettings';

const settings = JSON.parse(localStorage.getItem("settings"));

// ── SVG Gradient Gauge ────────────────────────────────────────────────────────
// Semi-circle: left (9 o'clock) = 0, top (12 o'clock) = 1.0, right (3 o'clock) = 2.0
const CX = 100, CY = 105, R = 75, SW = 8;
// sweep-flag=1 (clockwise in SVG screen space) → left → top → right = top arc ✓
const ARC_PATH = `M ${CX - R} ${CY} A ${R} ${R} 0 0 1 ${CX + R} ${CY}`;

const SvgGauge = ({ percent, id }) => {
    const clamped = Math.min(Math.max(percent, 0), 1);
    const angle = Math.PI * (1 - clamped); // π (left) → 0 (right)
    const nLen = 58;
    const nx = CX + nLen * Math.cos(angle);
    const ny = CY - nLen * Math.sin(angle); // SVG y is flipped

    return (
        <svg viewBox="0 0 200 115" style={{ width: 70, display: 'block' }}>
            <defs>
                {/* gradient runs along x-axis matching the arc's left/right endpoints */}
                <linearGradient
                    id={`gg-${id}`}
                    x1={CX - R} y1="0" x2={CX + R} y2="0"
                    gradientUnits="userSpaceOnUse"
                >
                    <stop offset="0%" stopColor="#EF4444" />
                    <stop offset="44%" stopColor="#F59E0B" />
                    <stop offset="56%" stopColor="#22C55E" />
                    <stop offset="100%" stopColor="#22C55E" />
                </linearGradient>
            </defs>
            {/* Track */}
            <path d={ARC_PATH} fill="none" stroke="#e5e7eb" strokeWidth={SW} strokeLinecap="round" />
            {/* Gradient arc (always full — shows the scale) */}
            <path d={ARC_PATH} fill="none" stroke={`url(#gg-${id})`} strokeWidth={SW} strokeLinecap="round" />
            {/* Needle */}
            <line x1={CX} y1={CY} x2={nx} y2={ny} stroke="#374151" strokeWidth={1.5} strokeLinecap="round" />
            {/* Needle base */}
            <circle cx={CX} cy={CY} r={3.5} fill="#374151" />
        </svg>
    );
};

// ── Tooltip ───────────────────────────────────────────────────────────────────
const formatValue = (value, isCurrency = false) => {
    if (value === null || value === undefined || isNaN(Number(value))) return '—';

    return (isCurrency ? `${getAccountCurrency(settings)} ` : '') + displayDecimalFormatted(settings, value);
};

// ── Draggable wrapper for pinned tooltips ─────────────────────────────────────
const DraggableTooltipWrapper = ({ initialX, initialY, onClose, children }) => {
    const [pos, setPos] = useState({ x: initialX, y: initialY });

    const handleDragStart = (e) => {
        if (e.target.closest('.kpi-tt-close')) return;
        e.preventDefault();
        const startX = e.clientX - pos.x;
        const startY = e.clientY - pos.y;
        const onMove = (me) => setPos({ x: me.clientX - startX, y: me.clientY - startY });
        const onUp = () => {
            document.removeEventListener('mousemove', onMove);
            document.removeEventListener('mouseup', onUp);
        };
        document.addEventListener('mousemove', onMove);
        document.addEventListener('mouseup', onUp);
    };

    return (
        <div
            className="kpi-gauge-tooltip kpi-gauge-tooltip--pinned"
            style={{ left: pos.x + 14, top: pos.y + 14 }}
            onMouseDown={(e) => e.stopPropagation()}
            onClick={(e) => e.stopPropagation()}
        >
            <div className="kpi-tt-drag-handle" onMouseDown={handleDragStart}>
                <GripVertical size={12} className="kpi-tt-grip" />
                <button className="kpi-tt-close" onClick={onClose}><X size={12} /></button>
            </div>
            {children}
        </div>
    );
};

const TooltipContent = ({ insight, value }) => {
    const statusColor = STATUS_COLOR[insight.status ?? 'red'];
    return (
        <>
            <div className="kpi-tt-header">
                <span className="kpi-tt-dot" style={{ background: statusColor }} />
                <strong>{insight.title}</strong>
            </div>
            <div className="kpi-tt-divider" />
            <div className="kpi-tt-row">
                <span className="kpi-tt-key">Value</span>
                <span className="kpi-tt-val">{formatValue(value)}</span>
            </div>
            <div className="kpi-tt-row">
                <span className="kpi-tt-key">Formula</span>
                <span className="kpi-tt-val kpi-tt-mono">{insight.formula}</span>
            </div>
            <div className="kpi-tt-divider" />
            <div className="kpi-tt-insight">{insight.insight}</div>
            <div className="kpi-tt-divider" />
            {insight.components.map((c) => (
                <div key={c.key} className="kpi-tt-component">
                    <span className="kpi-tt-comp-label">{c.label}</span>
                    <span className="kpi-tt-comp-value">{formatValue(c.value)}</span>
                    <span className="kpi-tt-comp-note">← {c.note}</span>
                </div>
            ))}
        </>
    );
};

const TooltipPanel = ({ insight, value, x, y }) => {
    if (!insight) return null;
    return (
        <div className="kpi-gauge-tooltip" style={{ left: x + 14, top: y + 14 }}>
            <TooltipContent insight={insight} value={value} />
        </div>
    );
};

// ── Hours Buffer Tooltip ─────────────────────────────────────────────────────
const HoursBufferContent = ({ data }) => {
    const bufferColor = STATUS_COLOR.green;
    const total = data?.hoursBuffer ?? 0;
    return (
        <>
            <div className="kpi-tt-header">
                <span className="kpi-tt-dot" style={{ background: bufferColor }} />
                <strong>Hours Buffer — Resource Breakdown</strong>
            </div>
            <div className="kpi-tt-divider" />
            <div className="kpi-tt-row">
                <span className="kpi-tt-key">Total</span>
                <span className="kpi-tt-val">{formatValue(total)} hrs</span>
            </div>
            <div className="kpi-tt-divider" />
            {(data?.detailedHoursBuffer ?? []).map((item) => (
                <div key={item.resource.id} className="kpi-tt-component kpi-tt-resource-row">
                    <span className="kpi-tt-comp-label">
                        <HoverCard
                            HoverCardTrigger={
                                <div className={"flex flex-row items-center gap-2"}>
                                    <Avatar
                                        name={item.resource.name}
                                        image={{ src: item.resource.image }}
                                        color="colorful"
                                        size={20} />
                                    <span className="kpi-hb-resource-trigger">{item.resource.name}</span>
                                </div>
                            }
                            AvatarId={item.resource.id}
                            AvatarName={item.resource.name}
                            AvatarImage={item.resource.image}
                            AvatarEmail={item.resource.email}
                            AvatarPhone={item.resource.phoneNumber}
                            AvatarIsExternal={item.resource.isExternal}
                        />
                    </span>
                    <span className="kpi-tt-comp-value">{formatValue(item.hoursBuffer)} hrs</span>
                </div>
            ))}
        </>
    );
};

const HoursBufferTooltip = ({ data }) => (
    <div className="kpi-gauge-tooltip kpi-hours-buffer-tooltip">
        <HoursBufferContent data={data} />
    </div>
);

// ── Section ───────────────────────────────────────────────────────────────────
const GAUGES = ['cpi', 'ler'];

const KpiGaugeSection = ({
    kpis,
    view,
    onViewChange,
    isSyncing,
    settings = { currencySymbol: '$', groupSeparator: ',', decimalSeparator: '.' },
}) => {
    const data = view === 'ytd' ? kpis?.ytd : kpis?.monthly;

    const [active, setActive] = useState(null);                // { kpi, x, y } — hover follows mouse
    const [pinned, setPinned] = useState(null);                // { kpi, x, y } — pinned & draggable
    const [hoursBufferHover, setHoursBufferHover] = useState(false);
    const [hoursBufferPinned, setHoursBufferPinned] = useState(null); // { x, y }

    const handleSectionMouseMove = (e) => {
        setActive(prev => prev ? { ...prev, x: e.clientX, y: e.clientY } : null);
    };

    const handleEnter = (kpi) => (e) => setActive({ kpi, x: e.clientX, y: e.clientY });
    const handleLeave = () => setActive(null);

    const handleCardClick = (kpi) => (e) => {
        e.stopPropagation();
        setPinned(prev => prev?.kpi === kpi ? null : { kpi, x: e.clientX, y: e.clientY });
        setActive(null);
    };

    const handleHoursBufferClick = (e) => {
        e.stopPropagation();
        setHoursBufferPinned(prev => prev ? null : { x: e.clientX, y: e.clientY });
        setHoursBufferHover(false);
    };

    const activeInsight = active ? getKpiInsight(active.kpi, data?.[active.kpi] ?? 0, data) : null;
    const pinnedInsight = pinned ? getKpiInsight(pinned.kpi, data?.[pinned.kpi] ?? 0, data) : null;

    return (
        <div className="kpi-gauge-section" onMouseMove={handleSectionMouseMove}>
            {/* Toggle */}
            <div className="kpi-toggle">
                <button className={view === 'ytd' ? 'active' : ''} onClick={() => onViewChange('ytd')}>YTD</button>
                <button className={view === 'monthly' ? 'active' : ''} onClick={() => onViewChange('monthly')}>Current Month</button>
            </div>

            {/* Legend */}
            <div className="kpi-legend">
                <span className="kpi-legend-item kpi-legend-item--planned">Planned</span>
                <span className="kpi-legend-item kpi-legend-item--actuals">Actuals</span>
            </div>

            {/* Visual separator */}
            <span className="kpi-gauge-separator" />

            {/* Gauges */}
            <div className="kpi-gauges-row">
                {GAUGES.map((kpi) => {
                    const value = data?.[kpi];
                    const percent = Math.min(Math.max((value ?? 0) / 2, 0), 1);
                    const insight = getKpiInsight(kpi, value ?? 0, data);
                    const statusColor = STATUS_COLOR[insight?.status ?? 'red'];
                    const isPinned = pinned?.kpi === kpi;
                    return (
                        <div
                            key={kpi}
                            className={`kpi-gauge-card kpi-gauge-card--${insight?.status}${isPinned ? ' kpi-gauge-card--pinned-active' : ''}`}
                            onMouseEnter={handleEnter(kpi)}
                            onMouseLeave={handleLeave}
                            onClick={handleCardClick(kpi)}
                        >
                            <SvgGauge percent={percent} id={kpi} />
                            <div className="kpi-gauge-footer">
                                <span className="kpi-gauge-label">{insight?.label}</span>
                                <span className="kpi-gauge-value" style={{ color: statusColor }}>
                                    {value !== null && value !== undefined ? formatValue(value) : '—'}
                                </span>
                            </div>
                        </div>
                    );
                })}
                <div
                    key='hoursBuffer'
                    className={`kpi-gauge-card ${Number(data?.hoursBuffer) >= 0 ? 'kpi-gauge-card--green' : 'kpi-gauge-card--red'} kpi-gauge-card--hours-buffer${hoursBufferPinned ? ' kpi-gauge-card--pinned-active' : ''}`}
                    onMouseEnter={() => setHoursBufferHover(true)}
                    onMouseLeave={() => setHoursBufferHover(false)}
                    onClick={handleHoursBufferClick}
                >
                    <div className="kpi-gauge-value-center">
                        <span className="kpi-gauge-value kpi-gauge-value--large" style={{ color: Number(data?.hoursBuffer) >= 0 ? STATUS_COLOR.green : STATUS_COLOR.red }}>
                            {data?.hoursBuffer !== null && data?.hoursBuffer !== undefined
                                ? formatValue(data?.hoursBuffer)
                                : '—'}
                        </span>
                    </div>
                    <div className="kpi-gauge-footer">
                        <span className="kpi-gauge-label">Hrs Buffer</span>
                    </div>
                    {hoursBufferHover && !hoursBufferPinned && <HoursBufferTooltip data={data} />}
                </div>
            </div>

            {isSyncing && (
                <span className="kpi-sync-indicator">
                    <Loader2 size={12} className="kpi-spinner" />
                    Syncing…
                </span>
            )}

            {/* Hover tooltip — hidden when that kpi is already pinned */}
            {active && active.kpi !== pinned?.kpi && (
                <TooltipPanel
                    insight={activeInsight}
                    value={data?.[active.kpi]}
                    data={data}
                    settings={settings}
                    x={active.x}
                    y={active.y}
                />
            )}

            {/* Pinned draggable tooltip for CPI / LER */}
            {pinned && pinnedInsight && (
                <DraggableTooltipWrapper
                    initialX={pinned.x}
                    initialY={pinned.y}
                    onClose={() => setPinned(null)}
                >
                    <TooltipContent
                        insight={pinnedInsight}
                        value={data?.[pinned.kpi]}
                        data={data}
                        settings={settings}
                    />
                </DraggableTooltipWrapper>
            )}

            {/* Pinned draggable tooltip for Hours Buffer */}
            {hoursBufferPinned && (
                <DraggableTooltipWrapper
                    initialX={hoursBufferPinned.x}
                    initialY={hoursBufferPinned.y}
                    onClose={() => setHoursBufferPinned(null)}
                >
                    <HoursBufferContent data={data} />
                </DraggableTooltipWrapper>
            )}
        </div>
    );
};

export default KpiGaugeSection;
